import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useState, type FormEvent } from 'react'
import { PathwayGroup } from '../../app/hooks/UsePathways.ts'
import { PathwayCode, SpeechDetails } from '../../types/Types.ts'

export interface SpeechDetailsDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (details: SpeechDetails) => void
  groups: PathwayGroup[]
  isSubmitting: boolean
  initialValues?: SpeechDetails
  title?: string
  description?: string
  submitLabel?: string
}

const PATHWAY_LEVELS = [1, 2, 3, 4, 5]

const SpeechDetailsDialog = ({
  isOpen,
  onClose,
  onSubmit,
  groups,
  isSubmitting,
  initialValues,
  title: dialogTitle = 'Speech details',
  description: dialogDescription = "Tell us about the speech you'll be giving for this meeting.",
  submitLabel = 'Confirm speech',
}: SpeechDetailsDialogProps) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [pathway, setPathway] = useState<PathwayCode | ''>('')
  const [level, setLevel] = useState<string>('')

  // Reset the form whenever the dialog is (re)opened so a previous attempt
  // doesn't leak into the next booking. Seed from initialValues in edit mode;
  // fall back to blank when none are provided (booking flow).
  useEffect(() => {
    if (isOpen) {
      setTitle(initialValues?.speechTitle ?? '')
      setDescription(initialValues?.speechDescription ?? '')
      setPathway(initialValues?.pathway ?? '')
      setLevel(
        initialValues?.pathwayLevel ? String(initialValues.pathwayLevel) : ''
      )
    }
  }, [isOpen, initialValues])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmit({
      speechTitle: title.trim() || undefined,
      speechDescription: description.trim() || undefined,
      pathway: pathway || undefined,
      pathwayLevel: level ? Number(level) : undefined,
    })
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-5 py-6 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95">
            <div className="flex items-start justify-between">
              <DialogTitle
                as="h3"
                className="text-lg font-semibold text-gray-900">
                {dialogTitle}
              </DialogTitle>
              <button
                type="button"
                onClick={onClose}
                className="-m-2 rounded-md p-2 text-gray-400 hover:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">{dialogDescription}</p>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="speech-title"
                  className="block text-sm font-medium text-gray-900">
                  Speech title{' '}
                  <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <input
                  id="speech-title"
                  name="speechTitle"
                  type="text"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Enter the title of your speech"
                  className="mt-1 block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600"
                />
              </div>

              <div>
                <label
                  htmlFor="speech-description"
                  className="block text-sm font-medium text-gray-900">
                  Description{' '}
                  <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <textarea
                  id="speech-description"
                  name="speechDescription"
                  rows={3}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Add a description"
                  className="mt-1 block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="speech-pathway"
                    className="block text-sm font-medium text-gray-900">
                    Pathway{' '}
                    <span className="font-normal text-gray-500">(optional)</span>
                  </label>
                  <select
                    id="speech-pathway"
                    name="pathway"
                    value={pathway}
                    onChange={(event) =>
                      setPathway(event.target.value as PathwayCode | '')
                    }
                    className="mt-1 block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600">
                    <option value="">Select a pathway</option>
                    {groups.map(({ group, pathways }) => (
                      <optgroup key={group} label={group}>
                        {pathways.map((pathwayOption) => (
                          <option
                            key={pathwayOption.code}
                            value={pathwayOption.code}>
                            {pathwayOption.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="speech-level"
                    className="block text-sm font-medium text-gray-900">
                    Level{' '}
                    <span className="font-normal text-gray-500">(optional)</span>
                  </label>
                  <select
                    id="speech-level"
                    name="pathwayLevel"
                    value={level}
                    onChange={(event) => setLevel(event.target.value)}
                    className="mt-1 block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600">
                    <option value="">Select a level</option>
                    {PATHWAY_LEVELS.map((pathwayLevel) => (
                      <option key={pathwayLevel} value={pathwayLevel}>
                        Level {pathwayLevel}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                  {isSubmitting ? 'Saving...' : submitLabel}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

export default SpeechDetailsDialog
